import { BaseController } from 'controllers/BaseController'
import { Profile } from 'model/User'
import { AuthService } from 'services/auth'
import { SegmentService, TelemetryService } from 'services/Telemetry'
import store from 'store/configureStore'
import { newUserActions } from 'store/User/newUserReducer'
import { IUserAPI } from './PartyAccessController'
import { UserAPI } from './UserAPI'

export class UserController extends BaseController<IUserAPI> {
  constructor() {
    super((onRequestChange) => new UserAPI(onRequestChange))
  }

  private setLastVisitedWorkspace(partyId: string) {
    localStorage.setItem('rh24_lastVisitedWorkspaceId', partyId)
  }

  public getLastVisitedWorkspace() {
    return localStorage.getItem('rh24_lastVisitedWorkspaceId')
  }

  public async SaveProfile(profile: Partial<Profile>) {
    try {
      const resp = await this.api.SaveProfile(profile)
      store.dispatch(newUserActions.setUserProfile({ userProfile: resp }))

      return resp
    } catch (err) {
      this.HandleError(err)
      this.GetProfile({ disableRedirect: true })
      throw err
    }
  }

  public async GetProfile(
    options: {
      returnUrl?: string
      disableRedirect?: boolean
      onNavigate?: (returnUrl: string) => void
    } = {}
  ) {
    try {
      const user = await this.api.GetProfile()

      store.dispatch(
        newUserActions.setUserProfile({
          userProfile: user,
          auth0User: AuthService.GetCurrent().GetAuth0User(),
        })
      )

      TelemetryService.getInstance().IdentifyUser(user)

      if (
        user.partiesDto?.length === 0 &&
        user.availablePortals?.length === 1
      ) {
        let formattedReturnUrl = options?.returnUrl?.replace('/app', '')
        if (formattedReturnUrl) {
          formattedReturnUrl = '#' + formattedReturnUrl
        }

        window.location.replace(
          `${user.availablePortals[0].url}${formattedReturnUrl}`
        )
      }

      if (!options?.disableRedirect) {
        const lastVisitedWorkspaceId = this.getLastVisitedWorkspace()
        if (lastVisitedWorkspaceId) {
          try {
            await this.SelectWorkspace(lastVisitedWorkspaceId)
          } catch {
            this.setLastVisitedWorkspace('')
          }
        } else if (user.partiesDto?.length === 1) {
          await this.SelectWorkspace(user.partiesDto[0].id)
        }

        if (options?.onNavigate) {
          options.onNavigate(options.returnUrl || '/app')
        } else {
          window.location.replace(options.returnUrl || '/app')
        }
      }

      return user
    } catch (ex) {
      this.HandleError(ex)
      if (ex['message']?.toLowerCase() === 'invalid user') {
        window.location.replace('/app/error?errorType=invalidUser')
      } else {
        window.location.replace('/app/error?errorType=maintenance')
      }

      throw ex
    }
  }

  public async SelectWorkspace(partyId: string) {
    try {
      let partyList = store.getState().user.organizations

      if (!partyList) {
        const user = await this.GetProfile()

        if (user.partiesDto?.length === 0) {
          throw new Error('No workspace context found')
        }

        partyList = user.partiesDto
      }

      const party = partyList.find((p) => p.id === partyId)

      if (!party) {
        throw new Error('User does not have access to this workspace')
      }

      store.dispatch(newUserActions.setOrganizationContext(party))

      const auth0User = AuthService.GetCurrent().GetAuth0User()

      if (auth0User) {
        SegmentService.getInstance().identify(
          auth0User.sub,
          auth0User.email,
          partyId
        )
      }

      document.title = party.name

      if (window.parent) {
        window.parent?.postMessage(
          {
            type: 'RH24_EMBEDDED_DOCUMENT_TITLE',
            payload: party.name,
          },
          '*'
        )
      }

      this.setLastVisitedWorkspace(partyId)

      return party
    } catch (ex) {
      throw this.HandleError(ex)
    }
  }

  public async ClearUserOrganizationContext() {
    try {
      store.dispatch(newUserActions.clearOrganizationContext())
      this.setLastVisitedWorkspace('')
    } catch (ex) {
      throw this.HandleError(ex)
    }
  }

  public async ClearLastVisitedWorkspace() {
    try {
      this.setLastVisitedWorkspace('')
    } catch (ex) {
      throw this.HandleError(ex)
    }
  }

  public async Logout() {
    try {
      AuthService.GetCurrent().Logout()
    } catch (ex) {
      throw this.HandleError(ex)
    }
  }
}
